// Toast service for class components
class ToastService {
  constructor() {
    this.toastFunction = null;
  }

  // Method to set the toast function from a component that has access to useToast
  setToastFunction(toastFunction) {
    this.toastFunction = toastFunction;
  }

  // Generic toast method
  showToast({ title, description, status = 'info', duration = 3000, isClosable = true, position = 'top' }) {
    if (this.toastFunction) {
      this.toastFunction({
        title,
        description,
        status,
        duration,
        isClosable,
        position,
      });
    } else {
      // Fallback if toast is not available
      console.warn('Toast service not initialized');
      
    }
  }

  // Convenience methods for different types of toasts
  success(title, description = '', duration = 3000) {
    this.showToast({
      title,
      description,
      status: 'success',
      duration,
    });
  }

  error(title, description = '', duration = 5000) {
    this.showToast({
      title,
      description,
      status: 'error',
      duration,
    });
  }

  warning(title, description = '', duration = 4000) {
    this.showToast({
      title,
      description,
      status: 'warning',
      duration,
    });
  }

  info(title, description = '', duration = 3000) {
    this.showToast({
      title,
      description,
      status: 'info',
      duration,
    });
  }

  // Enhanced error handling methods
  networkError(customTitle = 'Connection Error', customDescription = 'Please check your internet connection and try again.') {
    this.error(customTitle, customDescription, 6000);
  }

  validationError(title = 'Invalid Input', description = 'Please check your input and try again.') {
    this.warning(title, description, 4000);
  }

  serverError(title = 'Server Error', description = 'Something went wrong on our end. Please try again in a moment.') {
    this.error(title, description, 6000);
  }

  authError(title = 'Authentication Required', description = 'Please log in to continue.') {
    this.error(title, description, 5000);
  }

  progressUpdate(title, description = '', showProgress = false) {
    this.success(title, description, showProgress ? 8000 : 4000);
  }

  // Goal-specific messages
  goalCreated(goalName) {
    this.success('Goal Created Successfully', `"${goalName}" has been added to your journey.`, 4000);
  }

  goalDeleted(goalName) {
    this.success('Goal Deleted', `"${goalName}" has been removed from your goals.`, 3000);
  }

  goalLoadFailed() {
    this.networkError('Failed to Load Goals', 'Unable to retrieve your goals. Please refresh and try again.');
  }

  // Exercise-specific messages
  exerciseCreated(exerciseName) {
    this.success('Exercise Created', `"${exerciseName}" has been added successfully.`, 3000);
  }

  exerciseUpdated(exerciseName) {
    this.success('Exercise Updated', `"${exerciseName}" has been updated successfully.`, 3000);
  }

  exerciseDeleted(exerciseName) {
    this.success('Exercise Deleted', `"${exerciseName}" has been removed.`, 3000);
  }

  exerciseCompleted(exerciseName, streak = null) {
    const description = streak ? `Great work! Current streak: ${streak} days` : 'Great work on completing this exercise!';
    this.success(`Exercise Completed: ${exerciseName}`, description, 4000);
  }

  exerciseGenerated() {
    this.success('Exercise Generated', 'A new personalized exercise has been created for you.', 4000);
  }

  exerciseGenerationFailed() {
    this.error('Exercise Generation Failed', 'Unable to generate exercise content. Please try again.', 5000);
  }

  contentGenerationFailed() {
    this.error('Content Generation Failed', 'Unable to generate practice content. Please try again later.', 5000);
  }

  // Navigation messages
  navigationFailed(direction) {
    this.warning('Navigation Not Available', `No ${direction} exercise available in this goal.`, 3000);
  }

  // Generic operation messages
  operationSuccess(operation, item = '') {
    const messages = {
      'create': `Created successfully${item ? `: ${item}` : ''}`,
      'update': `Updated successfully${item ? `: ${item}` : ''}`,
      'delete': `Deleted successfully${item ? `: ${item}` : ''}`,
      'save': `Saved successfully${item ? `: ${item}` : ''}`,
      'load': `Loaded successfully${item ? `: ${item}` : ''}`
    };
    this.success(messages[operation] || 'Operation completed', '', 3000);
  }

  operationFailed(operation, item = '', customMessage = '') {
    const messages = {
      'create': `Failed to create${item ? ` ${item}` : ''}`,
      'update': `Failed to update${item ? ` ${item}` : ''}`,
      'delete': `Failed to delete${item ? ` ${item}` : ''}`,
      'save': `Failed to save${item ? ` ${item}` : ''}`,
      'load': `Failed to load${item ? ` ${item}` : ''}`
    };
    this.error(messages[operation] || 'Operation failed', customMessage || 'Please try again later.', 5000);
  }

  // Progress and achievement messages
  streakAchievement(days) {
    if (days === 1) {
      this.success('First Day Complete!', 'You\'ve started your journey. Keep going!', 4000);
    } else if (days === 7) {
      this.success('Week Streak!', 'Amazing! You\'ve practiced for a full week.', 5000);
    } else if (days === 30) {
      this.success('Month Streak!', 'Incredible dedication! 30 days of consistent practice.', 6000);
    } else if (days % 10 === 0) {
      this.success(`${days}-Day Streak!`, 'Your consistency is paying off. Keep it up!', 4000);
    }
  }

  // Feature-specific error messages
  featureNotAvailable(feature = 'feature') {
    this.info('Feature Not Available', `${feature} is not available right now. Please try again later.`, 4000);
  }

  permissionDenied(action = 'perform this action') {
    this.warning('Permission Denied', `You don't have permission to ${action}.`, 4000);
  }

  // Development and debug messages (only in development)
  debug(title, description = '') {
    if (process.env.NODE_ENV === 'development') {
      this.info(`[DEBUG] ${title}`, description, 2000);
    }
  }
}

// Export a singleton instance
const toastService = new ToastService();
export default toastService;